import type {
  CancelCommerceIntentDto,
  CommerceIntentDto,
  CommerceQuoteDto,
  CommerceRequirementResponseDto,
  ConfirmCommerceIntentDto,
  CreateCommerceIntentDto,
  CreateCommerceQuoteDto,
  CommerceTargetRefDto,
} from "@zipper/customer-commerce-client";
import type { CustomerIdentitySeed } from "../../runtime/context";

export interface CheckoutRuntimeIdentity {
  orgId: number;
  surface: "public-embed" | "dashboard-customer";
  audience: string;
  viewerRevision: string;
  customer?: CustomerIdentitySeed;
}

export interface ResolvedCheckoutTarget {
  targetKey: string | null;
  participantSelectionKeys: readonly string[];
  revision: string;
  selectedTarget?: CommerceTargetRefDto;
  availableTargets?: readonly CommerceTargetRefDto[];
}

export interface ResolveTargetInput {
  runtime: CheckoutRuntimeIdentity;
  requestedTargetKey: string | null;
}

export interface QuoteInput {
  runtime: CheckoutRuntimeIdentity;
  request: CreateCommerceQuoteDto;
}

export interface RequirementsInput {
  runtime: CheckoutRuntimeIdentity;
  quote: CommerceQuoteDto;
  responses: readonly CommerceRequirementResponseDto[];
}

export interface RequirementsResult {
  responses: readonly CommerceRequirementResponseDto[];
  complete: boolean;
}

export interface ReservationInput {
  runtime: CheckoutRuntimeIdentity;
  quote: CommerceQuoteDto;
  target: ResolvedCheckoutTarget;
}

export interface ReservationResult {
  reservationKey: string;
  expiresAt: string;
}

export interface PaymentCollectionInput {
  runtime: CheckoutRuntimeIdentity;
  intent: CommerceIntentDto;
  paymentOptionKey: string | null;
}

/**
 * Payment providers remain behind the adapter boundary. The statechart only
 * receives an opaque, short-lived reference that the server can consume.
 */
export interface PaymentCollectionResult {
  paymentMethodToken: string | null;
}

export interface CreateIntentInput {
  runtime: CheckoutRuntimeIdentity;
  request: CreateCommerceIntentDto;
}

export interface ConfirmIntentInput {
  runtime: CheckoutRuntimeIdentity;
  request: ConfirmCommerceIntentDto;
}

export interface RecoverIntentInput {
  runtime: CheckoutRuntimeIdentity;
  intentId: string;
}

export interface CancelIntentInput {
  runtime: CheckoutRuntimeIdentity;
  request: CancelCommerceIntentDto;
}

export interface AuthenticationInput {
  runtime: CheckoutRuntimeIdentity;
  email: string;
  secret: string;
  mode: "password" | "code";
}

export interface AuthenticationResult {
  viewerRevision: string;
}

export interface AccountInput {
  runtime: CheckoutRuntimeIdentity;
  email: string;
  password: string;
}

export interface AccountResult {
  viewerRevision: string;
}

export interface CheckoutGateway {
  resolveTarget(
    input: ResolveTargetInput & { signal: AbortSignal },
  ): Promise<ResolvedCheckoutTarget>;
  quote(
    input: QuoteInput & { signal: AbortSignal },
  ): Promise<CommerceQuoteDto>;
  collectRequirements?(
    input: RequirementsInput & { signal: AbortSignal },
  ): Promise<RequirementsResult>;
  reserve?(
    input: ReservationInput & { signal: AbortSignal },
  ): Promise<ReservationResult>;
  collectPayment?(
    input: PaymentCollectionInput & { signal: AbortSignal },
  ): Promise<PaymentCollectionResult>;
  createIntent(
    input: CreateIntentInput & { signal: AbortSignal },
  ): Promise<CommerceIntentDto>;
  confirmIntent(
    input: ConfirmIntentInput & { signal: AbortSignal },
  ): Promise<CommerceIntentDto>;
  recoverIntent(
    input: RecoverIntentInput & { signal: AbortSignal },
  ): Promise<CommerceIntentDto>;
  cancelIntent(
    input: CancelIntentInput & { signal: AbortSignal },
  ): Promise<CommerceIntentDto>;
  authenticate?(
    input: AuthenticationInput & { signal: AbortSignal },
  ): Promise<AuthenticationResult>;
  createAccount?(
    input: AccountInput & { signal: AbortSignal },
  ): Promise<AccountResult>;
}

export type CheckoutFailureCode =
  | "aborted"
  | "timeout"
  | "network"
  | "blocked"
  | "authentication_required"
  | "requirements_incomplete"
  | "payment_failed"
  | "declined"
  | "quote_changed"
  | "recovery_required"
  | "invalid_terminal_result"
  | "unavailable";

export interface CheckoutFailure {
  code: CheckoutFailureCode;
  message: string;
  retryable: boolean;
  cause?: unknown;
}

export class CheckoutActorError extends Error {
  readonly failure: CheckoutFailure;

  constructor(failure: CheckoutFailure) {
    super(failure.message);
    this.name = "CheckoutActorError";
    this.failure = failure;
  }
}
